import { useTheme } from "@emotion/react";
import { debounce } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { ModuleLayout } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { ScrollButtons } from "@brexhq/next/src/components/shared/ScrollButtons/ScrollButtons";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import { CarouselCardChanged } from "@brexhq/next/src/telemetry/analyticsEvents";
import { Winda } from "@brexhq/next/src/utils/Winda";

import { Column, Grid, isFullWidthColumn } from "../../shared/Grid";

import {
  CardMeasurer,
  ScrollController,
  ScrollButtonContainer,
  InnerScroll,
  NonWebkitProgressContainer,
} from "./CarouselSwipeCards.elements";
import { ProgressSlider } from "./components/ProgressSlider";
import { CarouselCardController } from "./components/SwipeCardController";
import {
  calculateMaxNumberOfScrolls,
  getDefaultCardStyles,
  getScrollDirection,
  mapCard,
} from "./helpers";
import {
  type ScrollStyle,
  type SwipeCarouselModuleProps,
  TEST_ID_CARRIAGE,
  CarouselSwipeCardsProps,
} from "./types";

/**
 * Shows a row of cards that can be swiped to view more.
 * Data is in Reusable Modules under Swipe Cards.
 */

export const CarouselSwipeCardsController = ({
  cards,
  moduleLayout,
  cardWidth,
}: SwipeCarouselModuleProps) => {
  const { theme } = moduleLayout;
  const cardCount = cards.length;

  const slideContainer = React.useRef<HTMLDivElement>(null);

  const lastCard = useRef<HTMLDivElement>();
  const scrollingContainer = useRef<HTMLDivElement>(null);

  const [maxNumberOfScrolls, setMaxNumberOfScrolls] = useState(0);
  const [maxScrollRange, setMaxScrollRange] = useState(0);
  const [windowSize, setWindowSize] = useState(0);
  const [position, setPositionState] = useState(0);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [scrollType, setScrollType] = useState<
    "default" | "buttons" | "customScrollBar"
  >("default");

  const { trackEvent } = useAnalytics();
  const trackScroll = useCallback(
    (
      currentPosition: number,
      nextPosition: number,
      direction: "left" | "right" | "none",
      type?: ScrollStyle,
    ) => {
      trackEvent(
        new CarouselCardChanged({
          type: type ?? "scroll",
          direction,
          position: currentPosition,
          nextPosition,
        }),
      );
    },
    [trackEvent],
  );

  // Window Resize Handler
  useEffect(() => {
    let lastWidth = Winda.get("innerWidth") ?? 0;
    const debouncedHandleResize = debounce(function handleResize() {
      const currentWidth = Winda.get("innerWidth") ?? 0;
      // Only execute if width actually changed
      if (currentWidth !== lastWidth) {
        setContainerScroll(0);
        setWindowSize(currentWidth);
        lastWidth = currentWidth;
      }
    }, 300);

    window.addEventListener("resize", debouncedHandleResize);
    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
  });

  const measuredCardRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      lastCard.current = node;
    }
  }, []);

  // tracking max number of scrolls - relates to viewport width
  useEffect(() => {
    // need to grab this because it is different on different screen sizes
    const actualcardWidth = lastCard.current?.clientWidth ?? 0;
    const { maxScrolls, maxRange } = calculateMaxNumberOfScrolls({
      slideContainerPosition:
        slideContainer?.current?.getBoundingClientRect().left ?? 0,
      cardWidth: actualcardWidth,
      cardCount,
      offsetRight: actualcardWidth / 2,
    });

    setMaxNumberOfScrolls(maxScrolls);
    setMaxScrollRange(maxRange);
  }, [cardCount, slideContainer, windowSize, lastCard]);

  //
  // Event Handlers
  const setContainerScroll = useCallback(
    (nextPosition: number) => {
      const actualcardWidth = lastCard.current?.clientWidth ?? 0;
      if (scrollingContainer.current) {
        scrollingContainer.current.scrollLeft = nextPosition * actualcardWidth;
      }
    },
    [scrollingContainer, lastCard],
  );

  const handlePositionChange = useCallback(
    (nextPosition: number, scrollStyle: ScrollStyle) => {
      if (nextPosition !== position) {
        const direction = getScrollDirection(position, nextPosition);
        trackScroll(position, nextPosition, direction, scrollStyle);

        if (scrollStyle === "scrollButtons") {
          setContainerScroll(nextPosition);
          setScrollType("buttons");
        }
        setPositionState(nextPosition);
      }
    },
    [position, trackScroll, setContainerScroll],
  );

  const debouncedDefaultScroll = useMemo(
    () =>
      debounce((scroll, actualcardWidth, percent) => {
        // last scroll may not snap perfectly to a card
        // floor works for everything below maxNumberOfScrolls - 1
        const newPosition =
          scroll > (maxNumberOfScrolls - 1) * actualcardWidth + 1
            ? maxNumberOfScrolls
            : Math.floor(percent);

        handlePositionChange(newPosition, "scroll");
      }, 100),
    [maxNumberOfScrolls, handlePositionChange],
  );

  const handleScrollChange = useCallback(
    (e) => {
      const actualcardWidth = lastCard.current?.clientWidth ?? 0;
      const scroll = e.target.scrollLeft;

      const percent = scroll / actualcardWidth;
      if (scrollPercent !== percent && scrollType !== "customScrollBar") {
        setScrollPercent(percent);
      }

      if (scrollType === "default") {
        debouncedDefaultScroll(scroll, actualcardWidth, percent);
      }
    },
    [scrollType, scrollPercent, debouncedDefaultScroll],
  );

  const handleProgressDragEnd = useCallback(
    (percent) => {
      setScrollType("default");
      const finalScrollRange = maxScrollRange - Math.floor(maxScrollRange);
      const halfwayFinalScroll =
        finalScrollRange / 2 + Math.floor(maxScrollRange);
      setContainerScroll(
        percent > halfwayFinalScroll ? maxNumberOfScrolls : Math.round(percent),
      );
    },
    [setScrollType, setContainerScroll, maxScrollRange, maxNumberOfScrolls],
  );

  return (
    <Container overflowX="hidden" data-testid="envelope">
      <ModuleLayout {...moduleLayout}>
        <Grid>
          {/* style is just for Firefox */}
          <Column {...isFullWidthColumn} style={{ width: "100%" }}>
            <Container ref={slideContainer} width="100%">
              <ScrollController
                data-testid={TEST_ID_CARRIAGE}
                maxNumberOfScrolls={maxNumberOfScrolls}
                moduleTheme={theme}
                ref={scrollingContainer}
                onScroll={handleScrollChange}
                onMouseOver={() => {
                  if (scrollType === "buttons") {
                    setScrollType("default");
                  }
                }}
                shouldSnap={scrollType === "customScrollBar" ? false : true}
              >
                <InnerScroll
                  paddingRight={(lastCard.current?.clientWidth ?? 0) / 2}
                >
                  {cards.map((props, index) => {
                    const isLast = index === cardCount - 1;
                    return (
                      <CardMeasurer
                        key={`child + ${props.id}`}
                        ref={isLast ? measuredCardRef : null}
                      >
                        <CarouselCardController
                          {...props}
                          cardWidth={cardWidth}
                        >
                          {props.renderedCard}
                        </CarouselCardController>
                      </CardMeasurer>
                    );
                  })}
                </InnerScroll>
              </ScrollController>

              {maxNumberOfScrolls > 0 && (
                <Container display="flex">
                  <ScrollButtonContainer>
                    <ScrollButtons
                      theme={theme}
                      canScrollLeft={position > 0}
                      canScrollRight={position < maxNumberOfScrolls}
                      onScrollLeft={() =>
                        handlePositionChange(position - 1, "scrollButtons")
                      }
                      onScrollRight={() =>
                        handlePositionChange(position + 1, "scrollButtons")
                      }
                    />
                  </ScrollButtonContainer>
                  <NonWebkitProgressContainer>
                    <ProgressSlider
                      scrollIndex={scrollPercent}
                      moduleTheme={moduleLayout.theme}
                      onDragEnd={handleProgressDragEnd}
                      onSliderDrag={setContainerScroll}
                      onDragStart={() => setScrollType("customScrollBar")}
                      maxRange={maxScrollRange}
                    />
                  </NonWebkitProgressContainer>
                </Container>
              )}
            </Container>
          </Column>
        </Grid>
      </ModuleLayout>
    </Container>
  );
};

export const CarouselSwipeCards = ({ data }: CarouselSwipeCardsProps) => {
  const theme = useTheme();
  const { width, margin } = getDefaultCardStyles(theme.spacing);

  const cards = data.cards.map((sanityCard, index) =>
    mapCard(
      data.moduleLayout.theme,
      data.alignment,
      sanityCard,
      index,
      width,
      margin,
      data.cardTheme,
    ),
  );

  return (
    <CarouselSwipeCardsController
      moduleLayout={data.moduleLayout}
      cards={cards}
      cardWidth={{
        LRG: width.LRG + margin.LRG,
        SML: width.SML + margin.SML,
      }}
    />
  );
};
